import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import "./bookEvents.css";
import BeforeFooter from "../components/layout/BeforeFooter";
import { EVENTS, EVENTS_PATH, getSeats } from "../data/events";

function BookEvents() {
  const [seats, setSeats] = useState({}); // { [eventId]: { limit, taken } }

  const upcoming = EVENTS.filter((e) => e.status === "upcoming");
  const past = EVENTS.filter((e) => e.status === "past");

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    upcoming
      .filter((e) => e.registrationOpen)
      .forEach(async (event) => {
        try {
          const data = await getSeats(event);
          setSeats((s) => ({ ...s, [event.id]: data }));
        } catch {
          /* counter stays hidden if the server is unreachable */
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seatsText = (event) => {
    if (event.type === "fair") return event.cardNote;
    if (!event.registrationOpen) return "Reservations open soon";
    const s = seats[event.id];
    if (!s) return `Limited to ${event.limit} seats`;
    const left = Math.max(s.limit - s.taken, 0);
    return left === 0 ? "Fully booked" : `${left} of ${s.limit} seats left`;
  };

  return (
    <>
      {/* HERO */}
      <section className="events-hero">
        <div className="events-hero-text">
          <p className="events-eyebrow">{upcoming.length > 0 ? "Upcoming" : "Coming Soon"}</p>
          <h1>Book Events</h1>
          <p className="events-hero-sub">
            {upcoming.length > 0 ? (
              "Come say hi at the fair."
            ) : (
              <>
                Book signings, reading circles, and live conversations. <br />
                Something is being planned, stay close.
              </>
            )}
          </p>
        </div>
        <div className="events-hero-img">
          <div className="events-hero-placeholder">
            🎟️
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS (or the original card when nothing is announced) */}
      {upcoming.length > 0 ? (
        <section className="events-upcoming" data-aos="fade-up">
          <h2 className="events-upcoming-title">Upcoming events</h2>
          <div className="events-upcoming-list">
            {upcoming.map((event) => (
              <Link key={event.id} to={`${EVENTS_PATH}/${event.id}`} className="events-event-card">
                <span className="events-icon">🎟️</span>
                <span className="events-event-date">{event.date}</span>
                <span className="events-event-name">{event.name}</span>
                <span className="events-event-meta">
                  {event.type === "fair" ? (
                    <>
                      {event.time} · <span dir="auto">{event.fair.hall}</span> · {event.fair.stand}
                    </>
                  ) : (
                    <>
                      {event.time} · {event.venue}
                    </>
                  )}
                </span>
                <span className="events-event-seats">{seatsText(event)}</span>
                <span className="events-event-cta">
                  {event.type === "fair" ? "See the details" : "View details and reserve"}
                </span>
              </Link>
            ))}
          </div>
        </section>
      ) : (
        <section className="events-soon-section" data-aos="fade-up">
          <div className="events-soon-card">
            <span className="events-soon-icon">✦</span>
            <h2>Events are on their way</h2>
            <p>
              Loubna is currently planning in-person and online events — book signings,
              intimate reading sessions, and open conversations about writing and growth.
              Check back soon or follow on Instagram to be the first to know.
            </p>
            <a
              href="https://www.instagram.com/elbylit"
              target="_blank"
              rel="noopener noreferrer"
              className="events-ig-btn"
            >
              Follow on Instagram →
            </a>
          </div>
        </section>
      )}

      {/* PAST EVENTS */}
      {past.length > 0 && (
        <section className="events-past">
          <h2 className="events-types-title">Past events</h2>
          <div className="events-upcoming-list events-past-list">
            {past.map((event) => (
              <Link key={event.id} to={`${EVENTS_PATH}/${event.id}`} className="events-event-card">
                {event.photos[0] && (
                  <img className="events-past-thumb" src={event.photos[0].src} alt={event.photos[0].alt} loading="lazy" />
                )}
                <span className="events-event-date">{event.date}</span>
                <span className="events-event-name">{event.name}</span>
                <span className="events-event-meta">{event.venue}, {event.area}</span>
                <span className="events-event-cta">See the photos</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* WHAT TO EXPECT */}
      <section className="events-types">
        <h2 className="events-types-title">What to Expect</h2>
        <div className="events-types-grid">
          <div className="events-type-card" data-aos="fade-up">
            <span className="events-icon">✍️</span>
            <h3>Book Signings</h3>
            <p>Get your copy signed and share a moment with the author.</p>
          </div>
          <div className="events-type-card" data-aos="fade-up" data-aos-delay="50">
            <span className="events-icon">📖</span>
            <h3>Reading Circles</h3>
            <p>Intimate group sessions to read, reflect, and discuss together.</p>
          </div>
          <div className="events-type-card" data-aos="fade-up" data-aos-delay="100">
            <span className="events-icon">🎙️</span>
            <h3>Live Conversations</h3>
            <p>Open discussions about writing, growth, and the journey behind the books.</p>
          </div>
        </div>
      </section>

      <BeforeFooter />
    </>
  );
}

export default BookEvents;